import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";

export const useSignAndLightsStore = defineStore("signAndLights", () => {
  let pageData = ref({});

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      isSuccess = await getInspLinkDataObject(brkey, inspectionId);
    }
    return isSuccess;
  };

  const getSignAndLights = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_SIGN_AND_LIGHTS;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(
          LOGGER.EventAction.Update,
          LOGGER.EventCat.RequestResponse,
          "Update Signs and Lights",
          response,
        );
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspLinkObject = (signAndLights) => {
    return {
      signLightInspection: {
        brkey: signAndLights.BRKEY,
        inspectionId: signAndLights.INSPECTIONID,
        baseCondRate: signAndLights.BASE_COND_RATE,
        columnCondRate: signAndLights.COLUMN_COND_RATE,
        grailCondRate: signAndLights.GRAIL_COND_RATE,
        accessCondRate: signAndLights.ACCESS_COND_RATE,
        signCondRate: signAndLights.SIGN_COND_RATE,
        lightCondRate: signAndLights.LIGHT_COND_RATE,
        surfaceCondRate: signAndLights.SURFACE_COND_RATE,
        horCondRate: signAndLights.HOR_COND_RATE,
        signInsptype: signAndLights.SIGN_INSPTYPE,
        nextSignInsptype: signAndLights.NEXT_SIGN_INSPTYPE,
        strRating: signAndLights.STRRATING,
        signAssetTag: signAndLights.SIGN_ASSET_TAG,
      },
      inspectionComments: signAndLights.inspectionComments,
    };
  };

  const convertToSaveInspModuleObject = (signAndLights, structure) => {
    structure.signLightInspection.baseCondRate = signAndLights.BASE_COND_RATE;
    structure.signLightInspection.columnCondRate = signAndLights.COLUMN_COND_RATE;
    structure.signLightInspection.grailCondRate = signAndLights.GRAIL_COND_RATE;
    structure.signLightInspection.accessCondRate = signAndLights.ACCESS_COND_RATE;
    structure.signLightInspection.signCondRate = signAndLights.SIGN_COND_RATE;
    structure.signLightInspection.lightCondRate = signAndLights.LIGHT_COND_RATE;
    structure.signLightInspection.surfaceCondRate = signAndLights.SURFACE_COND_RATE;
    structure.signLightInspection.horCondRate = signAndLights.HOR_COND_RATE;
    structure.signLightInspection.signInspType = signAndLights.SIGN_INSPTYPE;
    structure.signLightInspection.nextSignInspType = signAndLights.NEXT_SIGN_INSPTYPE;
    structure.signLightInspection.strRating = signAndLights.STRRATING;
    structure.signLightInspection.signAssetTag = signAndLights.SIGN_ASSET_TAG;
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    //clear idb object
    pageData.value = {};
    let isSuccess = false;
    let url = INSPECTION_LINKS_ENDPOINT.GET_SIGN_AND_LIGHTS.replace("{brkey}", brkey).replace(
      "{inspectionId}",
      inspectionId,
    );

    await axios
      .get(url)
      .then((resp) => {
        isSuccess = true;
        convertToInspLinkObject(resp.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
    return isSuccess;
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      BRKEY: structure.structureBridge.brkey,
      INSPECTIONID: structure.inspection.inspectionId,
      ACCESS_COND_RATE: structure?.signLightInspection?.accessCondRate,
      BASE_COND_RATE: structure?.signLightInspection?.baseCondRate,
      COLUMN_COND_RATE: structure?.signLightInspection?.columnCondRate,
      GRAIL_COND_RATE: structure?.signLightInspection?.grailCondRate,
      HOR_COND_RATE: structure?.signLightInspection?.horCondRate,
      LIGHT_COND_RATE: structure?.signLightInspection?.lightCondRate,
      SURFACE_COND_RATE: structure?.signLightInspection?.surfaceCondRate,
      NEXT_SIGN_INSPTYPE: structure?.signLightInspection?.nextSignInspType,
      SIGN_COND_RATE: structure?.signLightInspection?.signCondRate,
      SIGN_INSPTYPE: structure?.signLightInspection?.signInspType,
      STRRATING: structure?.signLightInspection?.strRating,
      SIGN_ASSET_TAG: structure?.signLightInspection?.signAssetTag,
    };
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      BRKEY: response.signLightInspection.brkey,
      INSPECTIONID: response.signLightInspection.inspectionId,
      BASE_COND_RATE: response.signLightInspection.baseCondRate,
      COLUMN_COND_RATE: response.signLightInspection.columnCondRate,
      GRAIL_COND_RATE: response.signLightInspection.grailCondRate,
      ACCESS_COND_RATE: response.signLightInspection.accessCondRate,
      SIGN_COND_RATE: response.signLightInspection.signCondRate,
      LIGHT_COND_RATE: response.signLightInspection.lightCondRate,
      SURFACE_COND_RATE: response.signLightInspection.surfaceCondRate,
      HOR_COND_RATE: response.signLightInspection.horCondRate,
      SIGN_INSPTYPE: response.signLightInspection.signInspType,
      NEXT_SIGN_INSPTYPE: response.signLightInspection.nextSignInspType,
      STRRATING: response.signLightInspection.strRating,
      SIGN_ASSET_TAG: response.signLightInspection.signAssetTag,
      inspectionComments: response.inspectionComments,
    };
  };

  return {
    getSignAndLights,
    saveData,
    loadDataObject,
  };
});
